import { useEffect, useRef } from 'react'

import { Link } from '@tanstack/react-router'
import { ArrowRight, Bell, Inbox } from 'lucide-react'

import {
  useMarkAllNotificationsRead,
  useNotifications,
} from '@/shared/hooks/useNotifications'

import { NotificationRow } from './NotificationRow'

interface NotificationDropdownProps {
  open: boolean
  onClose: () => void
  /** ref to the bell button — click-outside test ignores it */
  triggerRef: React.RefObject<HTMLElement | null>
}

const PREVIEW_COUNT = 10

// Header bell dropdown. Pops from the bell-icon button in app-header.
// Shows the last 10 notifications + Mark-all-read + "View all" link.
// Click-outside / Esc closes.
export function NotificationDropdown({ open, onClose, triggerRef }: NotificationDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  // Fresh fetch each time the dropdown opens (bypass staleTime). When
  // closed, the query goes quiet but remains cached for the badge.
  const { data, isPending, isError } = useNotifications({ page: 1, page_size: PREVIEW_COUNT })
  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllNotificationsRead()

  // Click-outside + Esc dismissal.
  useEffect(() => {
    if (!open) {return}

    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (
        panelRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return
      }
      onClose()
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {onClose()}
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, onClose, triggerRef])

  if (!open) {return null}

  const items = data?.items ?? []
  const unread = data?.unread_count ?? 0

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
      className="absolute right-0 top-full z-50 mt-2 w-96 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl ring-1 ring-black/5"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-gray-900">Notifications</p>
          {unread > 0 && (
            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700">
              {unread} new
            </span>
          )}
        </div>
        {items.some((n) => !n.read_at) && (
          <button
            type="button"
            onClick={() => markAllRead()}
            disabled={isMarkingAll}
            className="text-[11px] font-bold text-[#1a3db5] hover:underline disabled:opacity-50"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="max-h-[480px] overflow-y-auto">
        {isPending ? (
          <div className="space-y-2 px-3 py-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2.5 px-1 py-2">
                <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-slate-200" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-3 w-2/3 animate-pulse rounded bg-slate-200" />
                  <div className="h-2.5 w-4/5 animate-pulse rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="px-6 py-10 text-center">
            <p className="text-xs font-semibold text-red-700">Couldn&apos;t load notifications</p>
            <p className="mt-0.5 text-[11px] text-gray-500">Try again in a moment.</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center px-6 py-12 text-center">
            <Inbox className="mb-2 h-8 w-8 text-gray-300" />
            <p className="text-xs font-bold text-gray-700">No notifications yet</p>
            <p className="mt-0.5 text-[11px] text-gray-500">
              We&apos;ll let you know when there&apos;s something for you.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {items.map((n, i) => (
              <li
                key={n.id}
                className={i > 0 ? 'border-t border-gray-100' : ''}
              >
                <NotificationRow notification={n} onNavigate={onClose} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100">
        <Link
          to="/notifications"
          onClick={onClose}
          className="flex w-full items-center justify-center gap-1 px-4 py-2.5 text-xs font-bold text-[#1a3db5] hover:bg-blue-50/40"
        >
          View all <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  )
}

// Bell icon helper — re-export for app-header consumers.
export { Bell as NotificationBellIcon }
